import { useCallback, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TabPanel, TabPanels, Tabs } from '@chakra-ui/react';

import { AccessData } from './AccessData';
import { ProfessionalAndPersonalData } from './ProfessionalAndPersonalData';
import { NEW_TABS_INDEX } from './NewTabsHeader';
import { type AccessDataValues, type ProfessionalDataValues, defaultAccessData } from './types';
import { SignUpHelp } from './SignUpHelp';

import { Box } from 'src/components/UI/Box';
import { Flex } from 'src/components/UI/Flex';
import Typography from 'src/components/Typography';
import { Header } from 'src/components/Layout/Header';

import abstractBackground from 'src/assets/images/background-simple.png';
import { PARTNERS } from '../../Login/LoginBanner/LoginBanner';
import { EMSSignupBanner } from '../PartnerBanner/EMSSignupBanner';
import { useNavigateSearch } from 'src/helpers/useNavigateSearch';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { SignUpService, type DecodeTokenResponse } from 'src/services/signUp';
import { trackNewSignUpView } from 'src/services/tracking/events/signup.events';
import { trackingService } from 'src/services/tracking';
import { TrackerType } from 'src/services/tracking/enums';
import { PRODUCT_NAME } from 'src/services/tracking/events/constants';

export type { AccessDataValues, ProfessionalDataValues };

export function SignUp() {
  const [tabIndex, setTabIndex] = useState<NEW_TABS_INDEX>(NEW_TABS_INDEX.ACCESS_DATA);
  const [isFirstStepCompleted, setIsFirstStepCompleted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [accessData, setAccessData] = useState<AccessDataValues>(defaultAccessData);
  const [professionalData, setProfessionalData] = useState<ProfessionalDataValues | null>(null);
  const [autocompleteData, setAutocompleteData] = useState<DecodeTokenResponse['data'] | null>(null);
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);

  const navigateWithSearch = useNavigateSearch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openToast } = useFeedbackContext();

  const partner = searchParams.get('partner');
  const token = searchParams.get('token');
  const params = searchParams.get('params');

  useEffect(() => {
    trackNewSignUpView();
  }, []);

  useEffect(() => {
    if (token && !params) {
      trackingService.trackEvent(
        'QR_CODE',
        PRODUCT_NAME,
        {
          placement: 'side menu',
          surface: 'modal',
          flow: 'cadastro b2c via b2b',
          step: 'ir para cadastro',
          action: 'scan',
        },
        [TrackerType.MEVO_TRACKER]
      );
    }
  }, [token, params]);

  useEffect(() => {
    const fetchDecodeData = async () => {
      if (!token) {
        return;
      }

      setIsLoadingAutocomplete(true);
      try {
        const payload: { token: string; params?: string } = { token };
        if (params) {
          payload.params = params;
        }

        const response = await SignUpService.decodeToken(payload);

        if (response.data) {
          setAutocompleteData(response.data);
        }
      } catch (error) {
      } finally {
        setIsLoadingAutocomplete(false);
      }
    };

    fetchDecodeData();
  }, [token, params, openToast]);

  const handleAccessDataNext = useCallback((data: AccessDataValues) => {
    setAccessData(data);
    setIsFirstStepCompleted(true);
    setTabIndex(NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA);
  }, []);

  const handleAccessDataChange = useCallback((data: AccessDataValues) => {
    setAccessData(data);
  }, []);

  const handleProfessionalDataChange = useCallback((data: ProfessionalDataValues) => {
    setProfessionalData(data);
  }, []);

  const handleGoBackToAccessData = useCallback(() => {
    setTabIndex(NEW_TABS_INDEX.ACCESS_DATA);
  }, []);

  const handleGoBack = () => {
    if (showHelp) {
      setShowHelp(false);
      return;
    }

    try {
      localStorage.removeItem('personal-data-form');
      localStorage.removeItem('professional-data-form');
      localStorage.removeItem('place-service-form');
    } catch (e) {
      console.error(e);
    }

    if (token) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete('token');

      const queryString = newSearchParams.toString();
      const url = queryString ? `/?${queryString}` : '/';

      navigate(url, { replace: true });
    } else {
      navigateWithSearch('/');
    }
  };

  const handleShowHelp = useCallback(() => {
    setShowHelp(true);
  }, []);

  const PartnerBanner = {
    [PARTNERS.EMS]: <EMSSignupBanner />,
    DEFAULT: null,
  };

  return (
    <Box
      w="100%"
      h="100vh"
      bg="background"
      overflowY="auto"
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundImage={{ base: '', sm: `url(${abstractBackground})` }}
    >
      <Header onGoBack={handleGoBack} background="none" />
      {showHelp ? (
        <SignUpHelp onGoBack={() => setShowHelp(false)} />
      ) : (
        <Flex
          w="100%"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          p={{ base: '20px 20px 150px 20px', sm: '20px 20px' }}
        >
          {PartnerBanner[partner as keyof typeof PartnerBanner] || PartnerBanner[PARTNERS.DEFAULT]}
          <Flex
            w={{ base: '100%', sm: '450px' }}
            bg={{ base: 'background', sm: 'surface' }}
            borderRadius="30px"
            p={{ base: 0, sm: '50px' }}
            flexDir="column"
            mt={{ base: '26px', sm: '10px' }}
            boxShadow={{ base: 'none', sm: '12px 12px 40px rgba(61, 14, 107, 0.1)' }}
          >
            <Typography model="h1" weight="bold">
              Mevo Receita Digital
            </Typography>
            <Tabs isFitted index={tabIndex} variant="unstyled" isLazy={false}>
              <TabPanels>
                <TabPanel p={0} m={0}>
                  <AccessData
                    onNext={handleAccessDataNext}
                    onDataChange={handleAccessDataChange}
                    isStepCompleted={isFirstStepCompleted}
                    initialData={accessData}
                    phoneNumber={autocompleteData?.phoneNumber}
                    isLoadingPhoneNumber={isLoadingAutocomplete && !!token && !!params}
                  />
                </TabPanel>

                <TabPanel p={0} m={0}>
                  <ProfessionalAndPersonalData
                    accessData={accessData}
                    onGoBack={handleGoBackToAccessData}
                    onDataChange={handleProfessionalDataChange}
                    isFirstStepCompleted={isFirstStepCompleted}
                    initialData={professionalData}
                    autocompleteData={autocompleteData}
                    isLoadingAutocomplete={isLoadingAutocomplete}
                    hasToken={!!token}
                    onShowHelp={handleShowHelp}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
